import { useMemo, useState } from "react";
import { Flex, Heading, HStack, Icon, List } from "@chakra-ui/react";
import EmptyTable from "assets/icons/medium/empty-table-task.svg?react";
import { Empty } from "components/empty";
import { ImageGalleryModal } from "components/image-gallery/image-gallery-modal";
import { MaintenanceListCardSkeleton } from "components/maintenance-list/maintenance-list-card-skeleton";
import { MaintenanceTaskListItem } from "components/maintenance-list/maintenance-task-list-item";
import { ShowMoreLessButton } from "components/show-more-less-button";
import { TotalCountBadge } from "components/total-count-badge";
import { type MaintenanceTask } from "types/maintenance-task";

export type TenantMaintenanceListProps = {
  isLoading: boolean;
  maintenanceTasks: MaintenanceTask[];
};

export function TenantMaintenanceList({
  isLoading,
  maintenanceTasks,
}: TenantMaintenanceListProps) {
  const [showAllActive, setShowAllActive] = useState(false);
  const [showAllPast, setShowAllPast] = useState(false);

  const [currentOpenTaskId, setCurrentOpenTaskId] = useState<string | null>(
    null,
  );
  const [currentModalImage, setCurrentModalImage] = useState(0);

  const currentOpenTask = useMemo(
    () => maintenanceTasks?.find((task) => task.id === currentOpenTaskId),
    [currentOpenTaskId, maintenanceTasks],
  );

  const activeTasks: MaintenanceTask[] = useMemo(
    () =>
      isLoading
        ? Array.from({ length: 1 })
        : maintenanceTasks?.filter((task) => task.closed_at === null),
    [maintenanceTasks, isLoading],
  );

  const pastTasks: MaintenanceTask[] = useMemo(
    () =>
      isLoading
        ? Array.from({ length: 3 })
        : maintenanceTasks?.filter((task) => task.closed_at !== null),
    [maintenanceTasks, isLoading],
  );

  const pastTaskLength = pastTasks.length;
  const activeTaskLength = activeTasks.length;

  return (
    <>
      {/* start: Active Requests */}
      <Flex direction={"column"} gap={4}>
        <HStack gap={3}>
          <Heading fontSize={"xl"} as={"h2"}>
            Active requests
          </Heading>
          {isLoading ? null : <TotalCountBadge count={activeTasks?.length} />}
        </HStack>
        {activeTaskLength === 0 ? (
          <Empty
            icon={<Icon as={EmptyTable} boxSize={"auto"} color={"white"} />}
            emptyMessage={"Active Requests"}
            emptyDesc={
              "There are currently no active requests for this property"
            }
          />
        ) : (
          <List spacing={3}>
            {activeTasks
              ?.slice(0, showAllActive ? activeTasks?.length : 3)
              .map((task, index) =>
                isLoading ? (
                  <MaintenanceListCardSkeleton key={index} />
                ) : (
                  <MaintenanceTaskListItem
                    key={task.id}
                    task={task}
                    setCurrentOpenTaskId={setCurrentOpenTaskId}
                  />
                ),
              )}
          </List>
        )}
        {activeTasks?.length > 3 ? (
          <ShowMoreLessButton
            showAll={showAllActive}
            setShowAll={setShowAllActive}
          />
        ) : null}
      </Flex>
      {/* end: Active Requests */}

      {/* start: Past Requests */}
      <Flex direction={"column"} gap={4}>
        <HStack gap={3}>
          <Heading fontSize={"xl"} as={"h2"}>
            Past requests
          </Heading>
          {isLoading ? null : <TotalCountBadge count={pastTaskLength} />}
        </HStack>
        {pastTaskLength === 0 ? (
          <Empty
            icon={<Icon as={EmptyTable} boxSize={"auto"} color={"white"} />}
            emptyMessage={"Past Requests"}
            emptyDesc={"There are currently no past requests for this property"}
          />
        ) : (
          <List spacing={3}>
            {pastTasks
              ?.slice(0, showAllPast ? pastTaskLength : 3)
              ?.map((task, index) =>
                isLoading ? (
                  <MaintenanceListCardSkeleton key={index} />
                ) : (
                  <MaintenanceTaskListItem
                    key={task.id}
                    task={task}
                    setCurrentOpenTaskId={setCurrentOpenTaskId}
                  />
                ),
              )}
          </List>
        )}
        {pastTaskLength > 3 ? (
          <ShowMoreLessButton
            showAll={showAllPast}
            setShowAll={setShowAllPast}
          />
        ) : null}
      </Flex>
      {/* end: Past Requests */}
      <ImageGalleryModal
        isOpen={!!currentOpenTaskId}
        onClose={() => setCurrentOpenTaskId(null)}
        images={currentOpenTask?.images ?? []}
        currentModalImage={currentModalImage}
        setCurrentModalImage={setCurrentModalImage}
      />
    </>
  );
}
